/**
 * The CacheByArgumentStrings class caches objects in memory. Conceivably, we could have a
 * localStorage cache that would be persistent beyond the current session. The
 * cache is keyed by an arbitrary list of values, or more accurately an
 * arbitrary list of strings derived from the values you pass to the put method
 * (see the put method for details).
 */
export class CacheByArgumentStrings<T> {

    private cache: Record<string, T>;

    constructor() {
        this.cache = {};
    }

    /**
     * Stores the given item under a composite key formed from the given path
     * elements. For example: `cache.put(myobj, "name", 12)`.
     * @param {*} item - Arbitrary data. Note that you can perfectly well store
     * null or undefined if you want. But that's probably not what you want.
     * @param {...*} path - The remaining arguments should be data that uniquely
     * identifies the cached object. These arguments are all coerced into
     * strings, so avoid using Objects that just turn into a string like
     * "[object Object]", since that won't be unique.
     */
    put(item: T, ...path: unknown[]) {
        var key = JSON.stringify(path);
        this.cache[key] = item;
    }

    /**
     * Retrieve an item from the cache.
     * @param {...*} path - this should be the same set of values you passed to
     * the put method when you stored an item.
     * @returns The previously cached item, or null if it can't be found.
     * There's no error for keys that can't be found, and if you store a null
     * value there's no way to tell if null was stored intentionally or not.
     */
    get(...path: unknown[]): T | undefined {
        var key = JSON.stringify(path);
        if(key in this.cache){
            return this.cache[key];
        } else {
            return null;
        }
    }

    keys() {
        return Object.keys(this.cache);
    }

    values() {
        return Object.values(this.cache);
    }

    /**
     * Note that this memoize implementation is limited in the same way that
     * put and get are: the arguments to fn should be unique when turned into a
     * string.
     */
    static memoize<A extends string[], T>(fn: (...args: A) => T) {
        const cache: CacheByArgumentStrings<T> = new this();
        const wrapper = (...args: A) => {
            const key: string[] = args.map(x => String(x));
            var result = cache.get(...key);
            if(result) {
                return result;
            } else {
                result = fn(...args);
                cache.put(result, ...key);
                return result;
            }
        }
        return wrapper;
    }
}

export default CacheByArgumentStrings;

